import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const { isAdmin, isSchoolAdmin, isDistrict } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTeachers: 0,
    pendingUsers: 0,
    totalSchools: 0,
    totalDistricts: 0,
    totalEnrollments: 0,
    completionRate: 0,
    newEnrollmentsThisMonth: 0,
    totalCertificates: 0,
    totalCourses: 0,
    totalRevenue: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/admin/dashboard/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Define all possible stat cards
  const allStatCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
        </svg>
      ),
      accentColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      showFor: ['ADMIN'],
    },
    {
      title: 'Total Teachers',
      value: stats.totalTeachers,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
        </svg>
      ),
      accentColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      showFor: ['SCHOOL_ADMIN', 'DISTRICT'],
    },
    {
      title: 'Pending Verification',
      value: stats.pendingUsers,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      accentColor: 'text-amber-600',
      bgColor: 'bg-amber-50',
      showFor: ['ADMIN'],
    },
    {
      title: 'Total Schools',
      value: stats.totalSchools,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-12 18h12" />
        </svg>
      ),
      accentColor: 'text-slate-600',
      bgColor: 'bg-slate-50',
      showFor: ['ADMIN', 'DISTRICT'],
    },
    {
      title: 'Total Districts',
      value: stats.totalDistricts,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13l4-4 4 4M3 17l4-4 4 4M3 9l4-4 4 4m5 3h8m-8-4h8m-8-4h8" />
        </svg>
      ),
      accentColor: 'text-violet-600',
      bgColor: 'bg-violet-50',
      showFor: ['ADMIN'],
    },
    {
      title: 'Total Enrollments',
      value: stats.totalEnrollments,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      accentColor: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      showFor: ['ADMIN'],
    },
    {
      title: 'Certificates Issued',
      value: stats.totalCertificates,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      ),
      accentColor: 'text-rose-600',
      bgColor: 'bg-rose-50',
      showFor: ['ADMIN'],
    },
    {
      title: 'Completion Rate',
      value: `${stats.completionRate}%`,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
        </svg>
      ),
      accentColor: 'text-teal-600',
      bgColor: 'bg-teal-50',
      showFor: ['ADMIN'],
    },
    {
      title: 'New Enrollments (This Month)',
      value: stats.newEnrollmentsThisMonth,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      accentColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      showFor: ['ADMIN'],
    },
    {
      title: 'Total Courses',
      value: stats.totalCourses,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      accentColor: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      showFor: ['ADMIN'],
    },
  ];

  // Filter stat cards based on user role
  const getCurrentRole = () => {
    if (isAdmin) return 'ADMIN';
    if (isSchoolAdmin) return 'SCHOOL_ADMIN';
    if (isDistrict) return 'DISTRICT';
    return null;
  };

  const currentRole = getCurrentRole();
  const statCards = allStatCards.filter(card => 
    currentRole && card.showFor.includes(currentRole)
  );

  // Define quick actions based on role
  const allQuickActions = [
    {
      title: 'View Users',
      description: 'Manage all users',
      path: '/users',
      showFor: ['ADMIN', 'SCHOOL_ADMIN', 'DISTRICT'],
    },
    {
      title: 'Manage Schools',
      description: 'School administration',
      path: '/schools',
      showFor: ['ADMIN', 'SCHOOL_ADMIN', 'DISTRICT'],
    },
    {
      title: 'Course Content',
      description: 'Edit courses & lessons',
      path: '/courses',
      showFor: ['ADMIN'],
    },
    {
      title: 'View Certificates',
      description: 'Certificate management',
      path: '/certificates',
      showFor: ['ADMIN', 'SCHOOL_ADMIN', 'DISTRICT'],
    },
  ];

  const quickActions = allQuickActions.filter(action => 
    currentRole && action.showFor.includes(currentRole)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-primary mx-auto mb-3"></div>
          <p className="text-sm text-gray-500 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">Overview of platform statistics and key metrics</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((card, index) => (
          <div
            key={index}
            className="bg-white border border-gray-200 rounded-lg p-5 hover:border-gray-300 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{card.title}</p>
                <p className="text-2xl font-semibold text-gray-900">{card.value.toLocaleString()}</p>
              </div>
              <div className={`${card.bgColor} ${card.accentColor} p-2.5 rounded-lg`}>
                {card.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={() => navigate(action.path)}
              className="p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-left group"
            >
              <h3 className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-primary transition-colors">{action.title}</h3>
              <p className="text-xs text-gray-500">{action.description}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

